import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { FilmstripAmbience } from "./Ambience";
import { homeProjects } from "../data/projects";

function ProjectCard({ project }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <motion.article
      ref={ref}
      data-testid={`project-card-${project.id}`}
      data-cursor="view"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
      style={{ position: "relative" }}
      className={`group ${project.span}`}
    >
      <Link
        to={project.route}
        data-testid={`project-link-${project.id}`}
        className={`block relative overflow-hidden rounded-2xl ${project.aspect} bg-zinc-900 border border-white/[0.06]`}
        aria-label={`Voir le projet ${project.title}`}
      >
        <motion.img
          src={project.image}
          alt={project.title}
          loading="lazy"
          style={{ y }}
          className="absolute inset-0 w-full h-[120%] object-cover scale-105 transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent transition-opacity duration-500 group-hover:opacity-90" />
        <div className="absolute top-5 left-5 right-5 flex items-start justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">
          <span>{project.type}</span>
          <span>{project.year}</span>
        </div>
        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
          <div>
            <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-white leading-none mb-2">
              {project.title}
            </h3>
            <div className="flex flex-wrap gap-2">
              {project.tags.map((t) => (
                <span
                  key={t}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 px-2.5 py-1 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
          <span
            className="hidden sm:inline-flex w-12 h-12 rounded-full bg-[var(--accent)] text-black items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:rotate-45"
          >
            <ArrowUpRight className="w-5 h-5" strokeWidth={2} />
          </span>
        </div>
      </Link>
    </motion.article>
  );
}

export default function Projects() {
  return (
    <section
      id="projets"
      data-testid="projects-section"
      className="relative overflow-hidden py-28 sm:py-40 px-5 sm:px-8 lg:px-12 border-t border-white/[0.06]"
    >
      <FilmstripAmbience />
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 sm:mb-20 items-end">
        <div className="lg:col-span-7">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6 block">
            (04) — Projets sélectionnés
          </span>
          <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter">
            Conçus pour <br />
            <span className="text-outline">convertir</span> et performer.
          </h2>
        </div>
        <p className="lg:col-span-4 lg:col-start-9 text-zinc-400 leading-relaxed">
          Une sélection de projets qui mêlent identité forte, performance technique et orientation business. Tous pensés pour transformer le visiteur en client.
        </p>
      </div>

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-5">
        {homeProjects.map((p) => (
          <ProjectCard key={p.id} project={p} />
        ))}
      </div>

      <div className="relative z-10 mt-12 flex justify-center">
        <Link
          to="/portfolio"
          data-testid="projects-see-all"
          className="btn-pill group"
        >
          Voir tout le portfolio
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </section>
  );
}
